<template>
  <div class="attachments-preview" data-vjsf-prevent-collapse>
    <div class="attachments-preview-content">
      <div class="attachments-preview-header px-5 pt-3 pb-3 pr-5">
        <div class="attachments-preview-header-title">
          <span>{{ attachment.fileName || attachment.name }}</span>
        </div>
        <div class="attachments-preview-header-actions">
          <!-- download attachment btn -->
          <v-btn
            :ripple="false"
            class="attachments-preview-btn download-btn mr-3"
            text icon
            @click="downloadFile(attachment)"
          >
            <v-icon>
              mdi-download
            </v-icon>
          </v-btn>

          <!-- close attachment preview -->
          <v-btn
            :ripple="false"
            class="attachments-preview-header-btn"
            color="white" text
            icon
            @click.stop="handlePreviewClose"
          >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div class="attachments-preview-body px-5">
        <v-btn
          v-if="showPrevBtn"
          :ripple="false"
          class="preview-arrow-btn prev-btn pa-6"
          color="black"
          dark fab
          @click="showPreviousAttachment"
        >
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
        <div class="preview-body-content">
          <div v-if="isImage" class="preview-image mt-5 mb-3 mx-auto">
            <img v-if="imageUrl" :src="imageUrl" alt="Preview attachment Image">
            <v-progress-circular
              v-else
              indeterminate
              color="white"
              class="preview-loader"
            ></v-progress-circular>
          </div>

          <div v-else-if="isPdfFile" class="preview-pdf-wrapper mt-5 mx-auto">
            <v-progress-circular
              v-if="!pdfUrl && !pdfHasError"
              indeterminate
              color="white"
              class="preview-loader"
            ></v-progress-circular>

            <div v-else-if="!pdfUrl && pdfHasError" class="no-preview mt-0 text-center mx-auto pt-5 px-4">
              <p>There is no preview for this pdf document (try disabling any pdf downloading extensions)</p>
              <v-btn color="primary text-uppercase" @click="downloadFile(attachment)">
                Download
              </v-btn>
            </div>

            <PdfFilePreview v-else :pdf-url="pdfUrl" :num-pages="numPages" />
          </div>

          <div v-else class="no-preview text-center mx-auto pt-5">
            <p>There is no preview for this document</p>
            <v-btn color="primary text-uppercase" @click="downloadFile(attachment)">
              Download
            </v-btn>
          </div>
        </div>

        <v-btn
          v-if="showNextBtn"
          :ripple="false"
          class="preview-arrow-btn next-btn pa-6"
          color="black"
          dark fab
          @click="showNextAttachment"
        >
          <v-icon>
            mdi-arrow-right
          </v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>
<script>

import { getAttachmentContent } from '@/api/AttachmentApi';
import PdfFilePreview from '@/atoms/PdfFilePreview.vue';

async function unwrap(attachment, type) {
  if (attachment.file instanceof Blob) {
    return attachment.file;
  }

  if (attachment.file?.data) {
    // because the file might be serialized in some cases (before upload)
    return base64ToBlob(attachment.file.data, type);
  }

  if (attachment instanceof Blob) {
    return attachment;
  }
  const fileContent = await getAttachmentContent(attachment.path);
  return new Blob([fileContent], { type });
}

function base64ToBlob(data, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export default {
  name: 'AttachmentsPreview',
  components: {
    PdfFilePreview,
  },
  props: {
    attachment: {
      type: [Object, Blob],
      default: () => {},
    },
  },
  data() {
    return {
      imageUrl: '',
      allowedImageExtensions: ['png', 'jpeg', 'gif', 'jpg'],
      pdfUrl: '',
      numPages: 0,
      pdfHasError: false,
    };
  },
  computed: {
    isImage() {
      const fileExt = this.getFileExtension(this.attachment);

      return this.allowedImageExtensions.includes(fileExt);
    },
    isPdfFile() {
      const fileExt = this.getFileExtension(this.attachment);

      return fileExt === 'pdf';
    },
    showPrevBtn() {
      const index = this.currentAttachments.indexOf(this.attachment);

      return this.currentAttachments && this.currentAttachments.length > 1 && index > 0;
    },

    showNextBtn() {
      const index = this.currentAttachments.indexOf(this.attachment);

      return this.currentAttachments && this.currentAttachments.length > 1
      && index >= 0 && index < this.currentAttachments.length - 1;
    },
  },
  watch: {
    attachment: {
      handler(value) {
        this.updateImageUrl(value);
        this.updatePdfUrl(value);
      },
      immediate: true,
    },
  },
  methods: {
    updateImageUrl(attachment) {
      this.imageUrl = '';
      if (this.isImage) {
        this.getImageUrl(attachment);
      }
    },
    updatePdfUrl(attachment) {
      this.pdfUrl = '';
      this.pdfHasError = false;
      if (this.isPdfFile) {
        this.getPdfUrl(attachment);
      }
    },
    async getImageUrl(attachment) {
      const blob = await unwrap(attachment, 'image/*');
      this.imageUrl = URL.createObjectURL(blob);
    },
    async getPdfUrl(attachment) {
      let blob;
      try {
        blob = await unwrap(attachment, 'application/pdf');
      } catch (err) {
        if (err.status === 500 && err.message === 'Internal Server Error') {
          this.pdfHasError = true;
        }
        return;
      }
      this.pdfUrl = URL.createObjectURL(blob);
    },
    showPreviousAttachment() {
      const index = this.currentAttachments.indexOf(this.attachment);
      const prevIndex = index - 1;
      const prevAttachment = this.currentAttachments[prevIndex];
      this.$store.dispatch('updateSelectedAttachment', prevAttachment);
    },
    showNextAttachment() {
      const index = this.currentAttachments.indexOf(this.attachment);
      const nextIndex = index + 1;
      const nextAttachment = this.currentAttachments[nextIndex];
      this.$store.dispatch('updateSelectedAttachment', nextAttachment);
    },
    handlePreviewClose() {
      this.toggleShowAttachmentPreview(false, {}, []);
      this.imageUrl = '';
    },
    async downloadFile(attachment) {
      const blob = await unwrap(attachment, 'application/octet-stream');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = attachment.filename || attachment.fileName || attachment.name;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    getFileExtension(attachment) {
      const file = attachment.filename || attachment.fileName || attachment.name;
      return file.slice(file.lastIndexOf('.') + 1).toLowerCase();
    },
  },
};
</script>

<style lang="scss">
  .attachments-preview {
    width: 100%;
    background: rgba(46, 47, 78, 0.8);
    position: absolute;
    z-index: 210;
    height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .attachments-preview-content {
    position: relative;
  }
  .attachments-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: fixed;
    top: 0;
    background: rgba(46, 47, 78, 0.8);
    width: calc(100%);
    z-index: 50;
  }

  .attachments-preview-header-title {
    span {
      color: white;
      font-size: 15px;
      line-height: 24px;
    }
    .v-icon {
      color: white;
    }
  }

  .attachments-preview-btn {
    .v-icon {
      color: white !important;
    }
  }

  .preview-body-content {
    width: calc(100% - 70px);
    height: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .attachments-preview-body {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding-top: 60px;
    padding-bottom: 30px;
    height: calc(100vh - 5px);
    .preview-arrow-btn {
      width: 30px;
      height: 30px;
      position: fixed;
      top: 50%;
      &.next-btn  {
        right: 20px;
      }
    }
    .preview-pdf-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      width: calc(100% - 70px);
      .preview-loader {
        margin-top: 20%;
      }
    }
    .preview-image {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      .preview-loader {
        margin-top: 20%;
      }
    }
    .no-preview {
      width: 382px;
      height: 125px;
      background: #FFFFFF;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
      border-radius: 4px;
      p {
        font-size: 13px;
        line-height: 16px;
        color: #000;
      }
    }
  }

// RESPONSIVENESS
@media (max-width: 1024px) {
  .attachments-preview {
    .preview-image {
      width: 95%;
    }
  }
}

@media (max-width: 768px) {
  .attachments-preview {
    .attachments-preview-header {
      .attachments-preview-header-title {
        span {
          display: inline-block;
          max-width: 35ch;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
      }
      .attachments-preview-btn {
        display: none;
        &.download-btn {
          display: block;
        }
      }

      .attachments-preview-header-actions {
        margin-right: 0 !important;
      }
    }
    .preview-arrow-btn {
      &.prev-btn {
        left: 10px;
      }
      &.next-btn {
        right: 10px;
      }
    }
    .preview-body-content {
      width: calc(100% - 70px);
      margin-left: auto;
      margin-right: auto;
      & > * {
        width: 100% !important;
      }
    }
    .attachments-preview-body {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    .preview-image {
      width: 95%;
      height: 400px;
    }
  }
}
</style>
